import { FC, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Popup } from '@/components/UI/Popup/Popup';
import { cleanPhoneNumber, getLoginUrlWithReturn } from '@/utils/helpers';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  AuthIframeProps,
  AuthIframeEventName,
  AuthIframeEventData,
} from '@/components/AuthIframe/AuthIframe.types';
import { CONFIG } from '@/constants/config';
import useScreenSize from '@/hooks/useScreenSize';
import { CommonTestIds } from '@/__e2e__/testIds/common';

export const AuthIframe: FC<AuthIframeProps> = ({
  phones,
  isOpen,
  close,
  onSuccess,
  onManualClose,
}) => {
  const { isLarge } = useScreenSize();
  const router = useRouter();

  const authUrl = getLoginUrlWithReturn(
    router.asPath,
    router.asPath,
    phones.length ? cleanPhoneNumber(phones[0]) : undefined
  );

  const handleMessage = (
    event: MessageEvent<AuthIframeEventData | AuthIframeEventName>
  ) => {
    const eventHost = new URL(event.origin).host;

    if (eventHost !== CONFIG.authHost) return;

    const eventData = event.data;

    if (
      typeof eventData === 'object' &&
      'access_token' in eventData &&
      eventData?.name === AuthIframeEventName.Finished
    ) {
      const accessToken = eventData?.access_token;

      if (!accessToken) return;

      close();
      onSuccess(accessToken);
    } else if (event.data === AuthIframeEventName.Closed) {
      handleManualClose();
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  const renderIframe = () =>
    isOpen && (
      <iframe
        id="auth-iframe"
        src={authUrl}
        height="100%"
        width="100%"
        style={{ border: 'none' }}
        title="Authentication"
      />
    );

  function handleManualClose() {
    onManualClose?.();
    close();
  }

  return isLarge ? (
    <Popup
      isOpen={isOpen}
      close={handleManualClose}
      customSize={{ width: 680, height: 580 }}
      dataTestId={CommonTestIds.auth.iframe.popup}
    >
      {renderIframe()}
    </Popup>
  ) : (
    <Drawer
      isOpen={isOpen}
      isHeadHidden
      dataTestId={CommonTestIds.auth.iframe.drawer}
    >
      {renderIframe()}
    </Drawer>
  );
};
